import { LOG_MAX_ENTRIES } from '@mxa-argos/modules/forja/constants/forja.constants';
import type {
  ForjaLogEntry,
  ForjaLogKind,
} from '@mxa-argos/modules/forja/interfaces/forja-log-entry.interface';
import type { ForjaProgressEvent } from '@mxa-argos/modules/forja/interfaces/forja-progress-event.interface';
import type { ForjaShellLogEvent } from '@mxa-argos/modules/forja/interfaces/forja-shell-log-event.interface';

// Outside React, like the batch store: the log survives navigation and is bounded, so a full bench
// running for hours cannot grow without limit. A single timeline for the whole bench, because
// provisioning happens in batches and cross-device ordering is part of the diagnosis.
class ForjaLogStore {
  private entries: ForjaLogEntry[] = [];
  private readonly currentStepBySerial = new Map<string, string | null>();
  private readonly listeners = new Set<() => void>();
  private version = 0;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = (): number => this.version;

  allEntries(): readonly ForjaLogEntry[] {
    return this.entries;
  }

  appendShell(event: ForjaShellLogEvent): void {
    this.append(event.serial, 'shell', `$ ${event.command}`, {
      detail: event.error ?? (event.output || null),
      durationMs: event.durationMs,
    });
  }

  appendLifecycle(serial: string, text: string, detail?: string): void {
    this.append(serial, 'lifecycle', text, { detail: detail ?? null });
  }

  applyProgress(event: ForjaProgressEvent): void {
    switch (event.type) {
      case 'download': {
        const size = event.sizeBytes === null ? '' : ` (${event.sizeBytes} bytes)`;
        this.append(
          null,
          'event',
          `download ${event.packageName} [${event.index + 1}/${event.total}]: ${event.state}${size}`,
        );
        return;
      }
      case 'plan':
        this.currentStepBySerial.set(event.serial, null);
        this.append(event.serial, 'event', `plan received (${event.steps.length} steps)`);
        return;
      case 'phase-start':
        this.append(event.serial, 'event', `phase ${event.phase}`);
        return;
      case 'step-start':
        this.currentStepBySerial.set(event.serial, event.name);
        this.append(event.serial, 'event', `step ${event.name}`);
        return;
      case 'step-retry':
        this.append(
          event.serial,
          'event',
          `step ${event.name} retry ${event.attempt}/${event.maxAttempts}`,
        );
        return;
      case 'step-result': {
        const errorSuffix = event.result.errorCode ? ` (${event.result.errorCode})` : '';
        this.append(
          event.serial,
          'event',
          `step ${event.result.name} -> ${event.result.status}${errorSuffix}`,
          { detail: event.result.errorDetail ?? null },
        );
        this.currentStepBySerial.set(event.serial, null);
        return;
      }
      case 'wait-start':
        this.currentStepBySerial.set(event.serial, `wait ${event.reason}`);
        this.append(event.serial, 'event', `wait ${event.reason} started`);
        return;
      case 'wait-end':
        this.append(event.serial, 'event', 'wait ended');
        this.currentStepBySerial.set(event.serial, null);
        return;
      case 'install-progress':
        if (event.state !== 'transferring') {
          this.append(event.serial, 'event', `install ${event.packageName}: ${event.state}`);
        }
        return;
      case 'imei-read':
        this.append(event.serial, 'event', `imei read: ${event.imei}`);
        return;
      case 'finished':
        this.append(
          event.serial,
          'event',
          `finished: ${event.wasSuccessful ? 'success' : 'failure'}${
            event.errorCode ? ` (${event.errorCode})` : ''
          }`,
          { detail: event.errorDetail ?? null },
        );
        return;
    }
  }

  toText(): string {
    return this.entries
      .map((entry) => {
        const timestamp = new Date(entry.timestampMillis).toISOString();
        const serial = entry.serial ? ` [${entry.serial}]` : '';
        const step = entry.step ? ` [${entry.step}]` : '';
        const duration = entry.durationMs === null ? '' : ` (${entry.durationMs} ms)`;
        const header = `${timestamp}${serial}${step} ${entry.text}${duration}`;
        return entry.detail ? `${header}\n${indent(entry.detail)}` : header;
      })
      .join('\n');
  }

  private append(
    serial: string | null,
    kind: ForjaLogKind,
    text: string,
    extra?: { detail?: string | null; durationMs?: number },
  ): void {
    this.entries.push({
      timestampMillis: Date.now(),
      serial,
      kind,
      step: serial ? (this.currentStepBySerial.get(serial) ?? null) : null,
      text,
      detail: extra?.detail ?? null,
      durationMs: extra?.durationMs ?? null,
    });

    if (this.entries.length > LOG_MAX_ENTRIES) {
      this.entries.splice(0, this.entries.length - LOG_MAX_ENTRIES);
    }

    this.version += 1;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

function indent(detail: string): string {
  return detail
    .split('\n')
    .map((line) => `    ${line}`)
    .join('\n');
}

export const forjaLogStore = new ForjaLogStore();
